import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Firestore, collection, getDocs, limit, orderBy, query, where } from '@angular/fire/firestore';
import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSearchbar,
  IonText,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';

import { ADMIN_PAGES } from 'src/app/services/admin-pages.config';
import { OBJECT_MANAGER_FIELDS, OBJECT_MANAGER_OBJECT_KEYS } from 'src/app/contracts/object-manager.generated';

const AUDIT_LOG_FIELDS = OBJECT_MANAGER_FIELDS[OBJECT_MANAGER_OBJECT_KEYS.AUDIT_LOG];
const REPORT_INDEX_FIELDS = OBJECT_MANAGER_FIELDS[OBJECT_MANAGER_OBJECT_KEYS.REPORT_INDEX];
const SEARCH_INDEX_FIELDS = OBJECT_MANAGER_FIELDS[OBJECT_MANAGER_OBJECT_KEYS.SEARCH_INDEX];

type AdminSearchResultType = 'screen' | 'user' | 'report' | 'audit';

interface AdminSearchResult {
  id: string;
  type: AdminSearchResultType;
  title: string;
  subtitle: string;
  route: unknown[];
  badge?: string;
  icon: string;
  rank: number;
}

@Component({
  selector: 'app-admin-global-search-modal',
  templateUrl: './admin-global-search-modal.component.html',
  styleUrls: ['./admin-global-search-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonSearchbar,
    IonText,
    IonToolbar,
  ],
})
export class AdminGlobalSearchModalComponent implements OnInit {
  private modalCtrl = inject(ModalController);
  private router = inject(Router);
  private firestore = inject(Firestore);

  public searchQuery = '';
  public searchResults: AdminSearchResult[] = [];
  public recentSearches: string[] = [];
  public isSearching = false;

  private readonly storageKey = 'adminRecentSearches';
  private readonly pageResults = ADMIN_PAGES.filter(
    (page) =>
      (page.navigation.visible || page.header.enabled) &&
      page.pageType !== 'redirect' &&
      !page.navigation.external &&
      !page.path.includes(':')
  ).map((page) => ({
    id: `screen:${page.path}`,
    type: 'screen' as const,
    title: page.navigation.title || page.header.title || page.path,
    subtitle: page.navigation.section ? `${page.navigation.section} screen` : 'Admin screen',
    route: [`/${page.path}`],
    badge: page.navigation.badge,
    icon: page.navigation.icon || 'albums-outline',
    haystack: [page.path, page.navigation.title, page.navigation.section, page.header.title, page.header.subtitle]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  }));

  ngOnInit(): void {
    const stored = localStorage.getItem(this.storageKey);
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored);
      this.recentSearches = Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string').slice(0, 10) : [];
    } catch {
      this.recentSearches = [];
    }
  }

  public dismiss(): void {
    this.modalCtrl.dismiss();
  }

  public onSearchChange(event: CustomEvent): void {
    const searchTerm = String(event.detail?.value || '').trim();
    this.searchQuery = searchTerm;

    if (searchTerm.length < 2) {
      this.searchResults = [];
      this.isSearching = false;
      return;
    }

    void this.performSearch(searchTerm);
  }

  public async performSearch(searchTerm: string): Promise<void> {
    const requestTerm = searchTerm.trim();
    const normalized = this.normalizeToken(requestTerm);
    if (!normalized) {
      this.searchResults = [];
      return;
    }

    this.isSearching = true;

    try {
      const results: AdminSearchResult[] = [...this.searchScreens(requestTerm, normalized)];
      const [users, reports, auditEvents] = await Promise.all([
        this.searchUsers(normalized),
        this.searchReports(normalized),
        this.searchAuditEvents(normalized),
      ]);

      if (this.searchQuery.trim() !== requestTerm) return;

      this.searchResults = [...results, ...users, ...reports, ...auditEvents]
        .sort((a, b) => a.rank - b.rank || a.title.localeCompare(b.title))
        .slice(0, 24);
    } finally {
      if (this.searchQuery.trim() === requestTerm) {
        this.isSearching = false;
      }
    }
  }

  public selectResult(result: AdminSearchResult): void {
    this.saveRecentSearch(this.searchQuery);
    this.router.navigate(result.route);
    this.dismiss();
  }

  public selectRecentSearch(searchTerm: string): void {
    this.searchQuery = searchTerm;
    void this.performSearch(searchTerm);
  }

  public clearRecentSearches(): void {
    this.recentSearches = [];
    localStorage.removeItem(this.storageKey);
  }

  private searchScreens(searchTerm: string, normalized: string): AdminSearchResult[] {
    const raw = searchTerm.toLowerCase();

    return this.pageResults
      .filter((page) => page.haystack.includes(raw) || page.haystack.includes(normalized))
      .slice(0, 8)
      .map((page, index) => ({
        id: page.id,
        type: page.type,
        title: page.title,
        subtitle: page.subtitle,
        route: page.route,
        badge: page.badge,
        icon: page.icon,
        rank: index,
      }));
  }

  private async searchUsers(token: string): Promise<AdminSearchResult[]> {
    try {
      const snapshot = await getDocs(
        query(
          collection(this.firestore, 'usersIndex'),
          where(SEARCH_INDEX_FIELDS.TOKENS, 'array-contains', token),
          limit(6)
        )
      );

      return snapshot.docs.map((docSnap, index) => {
        const data = docSnap.data();
        const title = this.pickText(data['name'], data['userName'], data['email'], docSnap.id);
        const subtitle = this.pickText(data['email'], data['accountType'], data['status'], 'User account');

        return {
          id: `user:${docSnap.id}`,
          type: 'user',
          title,
          subtitle,
          route: ['/user-info', docSnap.id],
          icon: 'person-circle-outline',
          rank: 20 + index,
        };
      });
    } catch {
      return [];
    }
  }

  private async searchReports(token: string): Promise<AdminSearchResult[]> {
    try {
      const snapshot = await getDocs(
        query(
          collection(this.firestore, 'reportsIndex'),
          where(REPORT_INDEX_FIELDS.TOKENS, 'array-contains', token),
          limit(6)
        )
      );

      return snapshot.docs.map((docSnap, index) => {
        const data = docSnap.data();
        const targetId = this.pickText(data[REPORT_INDEX_FIELDS.TARGET_ID], docSnap.id);
        const reason = this.pickText(
          data[REPORT_INDEX_FIELDS.LAST_REASON],
          data[REPORT_INDEX_FIELDS.SUMMARY],
          data[REPORT_INDEX_FIELDS.TARGET_TYPE],
          'Report'
        );
        const status = this.pickText(data[REPORT_INDEX_FIELDS.STATUS], 'open');

        return {
          id: `report:${docSnap.id}`,
          type: 'report',
          title: `${reason}: ${targetId}`,
          subtitle: `Status: ${status}`,
          route: ['/user-reports', targetId],
          badge: status,
          icon: 'flag-outline',
          rank: 40 + index,
        };
      });
    } catch {
      return [];
    }
  }

  private async searchAuditEvents(token: string): Promise<AdminSearchResult[]> {
    try {
      const snapshot = await getDocs(
        query(
          collection(this.firestore, 'auditIndex'),
          where(SEARCH_INDEX_FIELDS.TOKENS, 'array-contains', token),
          orderBy(AUDIT_LOG_FIELDS.TIMESTAMP, 'desc'),
          limit(5)
        )
      );

      return snapshot.docs.map((docSnap, index) => {
        const data = docSnap.data();
        const action = this.pickText(
          data[AUDIT_LOG_FIELDS.ACTION],
          data[AUDIT_LOG_FIELDS.EVENT_TYPE],
          data[AUDIT_LOG_FIELDS.ACTION_TYPE],
          'Audit event'
        );
        const actor = this.pickText(
          data[AUDIT_LOG_FIELDS.USER_EMAIL],
          data[AUDIT_LOG_FIELDS.USER_NAME],
          data[AUDIT_LOG_FIELDS.USER_ID],
          'System'
        );
        const result = this.pickText(data[AUDIT_LOG_FIELDS.RESULT], data[AUDIT_LOG_FIELDS.SEVERITY]);

        return {
          id: `audit:${docSnap.id}`,
          type: 'audit',
          title: action,
          subtitle: actor,
          route: ['/audit-logs'],
          badge: result,
          icon: 'shield-checkmark-outline',
          rank: 60 + index,
        };
      });
    } catch {
      return [];
    }
  }

  private normalizeToken(value: string): string {
    return (
      value
        .replace(/[^\p{L}\p{N}\s]/gu, '')
        .trim()
        .toLowerCase()
        .split(/\s+/)[0] ?? ''
    );
  }

  private pickText(...values: unknown[]): string {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number' && Number.isFinite(value)) return String(value);
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    }
    return '';
  }

  private saveRecentSearch(searchTerm: string): void {
    const normalized = searchTerm.trim();
    if (!normalized) return;

    this.recentSearches = [normalized, ...this.recentSearches.filter((item) => item !== normalized)].slice(0, 10);
    localStorage.setItem(this.storageKey, JSON.stringify(this.recentSearches));
  }
}
